import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api/client';

export interface VideoManifest {
  streamUrl: string;
  mimeType: string;
  durationSeconds: number | null;
  width: number | null;
  height: number | null;
  posterUrl: string | null;
  expiresInSeconds: number;
  captions: Array<{ language: string; label: string; isDefault: boolean; url: string }>;
  chapters: Array<{ title: string; startSeconds: number }>;
  processing: { status: string; detail: string | null };
}

export interface WatchState {
  durationSeconds: number | null;
  coveredSeconds: number;
  coveragePercent: number;
  thresholdPercent: number;
  canComplete: boolean;
  segments?: Array<{ startSeconds: number; endSeconds: number }>;
}

interface Props {
  lessonId: string;
  video: VideoManifest;
  /** Where the learner left off, from the manifest. */
  resumeFrom: number;
  onWatchChange: (state: WatchState) => void;
}

/** BRD FR-VID-005. */
const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

/**
 * Position writes are throttled to at most one every ten seconds
 * (BRD FR-VID-004) and must never block the UI (NFR-PER-005).
 */
const REPORT_INTERVAL_MS = 10_000;

/**
 * The video stage.
 *
 * It reports the runs actually played rather than the playhead, because the
 * server measures coverage from those runs to decide completion
 * (BRD FR-VID-006). A seek ends the current run and starts a new one, so
 * skipping forward records a gap that no amount of further reporting can fill
 * without playing it.
 */
export function VideoStage({ lessonId, video, resumeFrom, onWatchChange }: Props) {
  const ref = useRef<HTMLVideoElement>(null);
  const [speed, setSpeed] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [resumed, setResumed] = useState(false);

  // The run currently being played, and the runs waiting to be sent.
  const runStart = useRef<number | null>(null);
  const lastTime = useRef(0);
  const pending = useRef<Array<{ startSeconds: number; endSeconds: number }>>([]);

  const closeRun = useCallback(() => {
    const start = runStart.current;
    const end = lastTime.current;
    runStart.current = null;
    if (start === null || end - start < 1) return;
    pending.current.push({ startSeconds: Math.floor(start), endSeconds: Math.ceil(end) });
  }, []);

  const flush = useCallback(
    async (alsoCloseRun: boolean) => {
      if (alsoCloseRun) closeRun();
      if (pending.current.length === 0) return;

      const segments = pending.current;
      pending.current = [];
      try {
        const state = await api<WatchState>(`/lessons/${lessonId}/watch`, {
          method: 'POST',
          body: JSON.stringify({
            segments,
            positionSeconds: Math.floor(ref.current?.currentTime ?? 0),
          }),
        });
        onWatchChange(state);
      } catch {
        // Put them back: losing a report would silently cost the learner
        // credit for time they actually watched.
        pending.current = [...segments, ...pending.current];
      }
    },
    [closeRun, lessonId, onWatchChange],
  );

  // Periodic flush while playing.
  useEffect(() => {
    const timer = window.setInterval(() => {
      if (document.hidden) return;
      closeRun();
      // Reopen the run so continuous playback is not chopped into gaps.
      if (!ref.current?.paused) runStart.current = ref.current?.currentTime ?? null;
      void flush(false);
    }, REPORT_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [closeRun, flush]);

  // Send what is outstanding when the lesson or the page goes away.
  useEffect(() => {
    const onHide = () => void flush(true);
    window.addEventListener('pagehide', onHide);
    document.addEventListener('visibilitychange', onHide);
    return () => {
      window.removeEventListener('pagehide', onHide);
      document.removeEventListener('visibilitychange', onHide);
      void flush(true);
    };
  }, [flush]);

  useEffect(() => {
    const element = ref.current;
    if (element) element.playbackRate = speed;
  }, [speed]);

  function seekTo(seconds: number) {
    const element = ref.current;
    if (!element) return;
    closeRun();
    element.currentTime = seconds;
    void element.play().catch(() => undefined);
  }

  const duration = video.durationSeconds ?? 0;

  return (
    <div className="vstage">
      {error ? <div className="alert alert--error">{error}</div> : null}

      <video
        ref={ref}
        className="stage__video"
        src={video.streamUrl}
        poster={video.posterUrl ?? undefined}
        controls
        playsInline
        preload="metadata"
        crossOrigin="use-credentials"
        onLoadedMetadata={() => {
          // Resume once, and only if there is somewhere sensible to resume to.
          if (resumed || !ref.current) return;
          if (resumeFrom > 2 && resumeFrom < (ref.current.duration || duration) - 2) {
            ref.current.currentTime = resumeFrom;
          }
          ref.current.playbackRate = speed;
          setResumed(true);
        }}
        onPlay={() => {
          runStart.current = ref.current?.currentTime ?? 0;
        }}
        onTimeUpdate={() => {
          lastTime.current = ref.current?.currentTime ?? 0;
        }}
        onSeeking={() => {
          // The run ends here; whatever is skipped is simply not reported.
          closeRun();
        }}
        onSeeked={() => {
          if (!ref.current?.paused) runStart.current = ref.current?.currentTime ?? 0;
        }}
        onPause={() => void flush(true)}
        onEnded={() => void flush(true)}
        onError={() =>
          setError(
            'The video could not be loaded. The playback link may have expired — reload the page.',
          )
        }
      >
        {video.captions.map((caption) => (
          <track
            key={caption.language}
            kind="subtitles"
            src={caption.url}
            srcLang={caption.language}
            label={caption.label}
            default={caption.isDefault}
          />
        ))}
      </video>

      <div className="vstage__bar">
        <label className="vstage__speed">
          <span className="muted">Speed</span>
          <select
            className="field__input"
            value={speed}
            onChange={(event) => setSpeed(Number(event.target.value))}
            aria-label="Playback speed"
          >
            {SPEEDS.map((rate) => (
              <option key={rate} value={rate}>
                {rate}×
              </option>
            ))}
          </select>
        </label>

        {video.durationSeconds ? (
          <span className="muted">
            {/* Run time only; the frame size means nothing to a learner. */}
            {formatClock(video.durationSeconds)}
          </span>
        ) : (
          <span className="muted">Duration unknown</span>
        )}
      </div>

      {video.chapters.length > 0 ? (
        <nav className="chapters" aria-label="Chapters">
          <h3 className="section__title">Chapters</h3>
          <ul>
            {video.chapters.map((chapter) => (
              <li key={`${chapter.startSeconds}-${chapter.title}`}>
                <button className="chapters__item" onClick={() => seekTo(chapter.startSeconds)}>
                  <span className="chapters__at">{formatClock(chapter.startSeconds)}</span>
                  <span>{chapter.title}</span>
                </button>
              </li>
            ))}
          </ul>
        </nav>
      ) : null}
    </div>
  );
}

export function formatClock(totalSeconds: number): string {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = seconds % 60;
  const pad = (value: number) => String(value).padStart(2, '0');
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(rest)}` : `${minutes}:${pad(rest)}`;
}
